import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { StructuredData, generateFAQSchema } from '@/components/StructuredData';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ArrowLeft, Search, User, Heart, Briefcase, Lightbulb, HelpCircle, Gem, Battery } from 'lucide-react';
import { entityBlueprint } from '@/config/entity-blueprint';

// FAQ categories with icons
const FAQ_CATEGORIES = [
  { id: 'founder', icon: User, labelKey: 'faq.categories.founder' },
  { id: 'trauma', icon: Heart, labelKey: 'faq.categories.trauma' },
  { id: 'challenge100', icon: Lightbulb, labelKey: 'faq.categories.challenge100' },
  { id: 'pearlFramework', icon: Gem, labelKey: 'faq.categories.pearlFramework' },
  { id: 'coldStart', icon: Battery, labelKey: 'faq.categories.coldStart' },
  { id: 'services', icon: Briefcase, labelKey: 'faq.categories.services' },
  { id: 'clarifications', icon: HelpCircle, labelKey: 'faq.categories.clarifications' },
];

export default function FAQ() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLocalizedPath();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const lang = i18n.language === 'zh' ? 'zh' : 'en';

  // Get FAQ items from translations
  const faqItems = t('faq.items', { returnObjects: true }) as Array<{
    id: string;
    category: string;
    question: string;
    answer: string;
  }>;

  // Filter FAQs based on search and category
  const filteredFaqs = Array.isArray(faqItems) ? faqItems.filter(item => {
    const matchesSearch = !searchQuery || 
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !activeCategory || item.category === activeCategory;
    return matchesSearch && matchesCategory;
  }) : [];

  // Generate FAQ schema for structured data
  const faqSchemaData = Array.isArray(faqItems) ? faqItems.map(item => ({
    question: item.question,
    answer: item.answer,
  })) : [];

  return (
    <div className="min-h-screen bg-background">
      <Navigation isHomePage={false} />
      
      {/* FAQ Page Schema */}
      <StructuredData type="website" />
      
      {/* Inject FAQPage structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema(faqSchemaData))
        }}
      />

      <main className="container mx-auto px-4 py-24 max-w-4xl">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '..' })}
          className="mb-8 -ml-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('common.back', 'Back')}
        </Button>

        {/* Header */}
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t('faq.title', 'Frequently Asked Questions')}
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('faq.subtitle', 'Find answers about Wang Haisheng, borninsea, trauma exploration, and Challenge 100 methodology.')}
          </p>
        </header>

        {/* Entity anchor for SEO */}
        <div className="bg-muted/30 rounded-lg p-4 mb-8 text-sm text-muted-foreground">
          {lang === 'zh' ? (
            <p>
              <strong>{entityBlueprint.founder.name}</strong>，
              {entityBlueprint.brand.nameZh}创始人，
              {entityBlueprint.founder.education.universityZh}（{entityBlueprint.founder.education.universityRank}）
              {entityBlueprint.founder.education.majorZh}硕士。
              拥有{entityBlueprint.metrics.yearsProduct.value}年产品经验和
              {entityBlueprint.metrics.yearsTrauma.value}年创伤探索历程。
            </p>
          ) : (
            <p>
              <strong>{entityBlueprint.founder.nameEn}</strong>, founder of {entityBlueprint.brand.name}, 
              holds a {entityBlueprint.founder.education.degree} in {entityBlueprint.founder.education.major} from {entityBlueprint.founder.education.university} ({entityBlueprint.founder.education.universityRank}). 
              With {entityBlueprint.metrics.yearsProduct.value} years of product experience and {entityBlueprint.metrics.yearsTrauma.value} years of trauma exploration.
            </p>
          )}
        </div>

        {/* Search bar */}
        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('faq.searchPlaceholder', 'Search questions...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Button
            variant={activeCategory === null ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveCategory(null)}
          >
            {t('faq.allCategories', 'All')}
          </Button>
          {FAQ_CATEGORIES.map(cat => (
            <Button
              key={cat.id}
              variant={activeCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveCategory(cat.id)}
            >
              <cat.icon className="mr-2 h-4 w-4" />
              {t(cat.labelKey)}
            </Button>
          ))}
        </div>

        {/* FAQ Accordion */}
        {filteredFaqs.length > 0 ? (
          <Accordion type="single" collapsible className="space-y-4">
            {filteredFaqs.map((item, index) => (
              <AccordionItem 
                key={item.id || index} 
                value={item.id || `faq-${index}`}
                className="border rounded-lg px-4"
              >
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="font-medium">{item.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground prose prose-sm max-w-none">
                  <p>{item.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <HelpCircle className="mx-auto h-12 w-12 mb-4 opacity-50" />
            <p>{t('faq.noResults', 'No questions found matching your search.')}</p>
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-16 text-center p-8 bg-muted/30 rounded-2xl">
          <h2 className="text-2xl font-bold mb-4">
            {t('faq.stillHaveQuestions', "Still have questions?")}
          </h2>
          <p className="text-muted-foreground mb-6">
            {t('faq.contactPrompt', "Can't find what you're looking for? Feel free to reach out.")}
          </p>
          <Button onClick={() => navigate({ to: getLocalizedPath('/#contact') })}>
            {t('faq.contactButton', 'Contact Me')}
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
